import { observer } from "mobx-react-lite";
import { KeyIcon } from "@heroicons/react/24/solid";
import { DownloadAsExcelSheet, FloatingMenu, SideOverlay, TailwindModal } from "../../components";
import { toast } from "react-toastify";
import { safe_get } from "../../report-visuals";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore, htmlValidation, excel_user_parts_col_def } from "../../helpers";
import { Avatar, Button, Divider, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { FilterOverLay, SearchVoterList, VoterListWithRadioButton } from "../voter-list";
import { excel_user_col_def, groupObjectsByKey, map_roles_by_key } from "./helpers";
import { RemoveUser } from "./remove-user";
import { BanUser } from "./ban-user";
import { UpdateUser } from "./update-user";
import ThumbnailImage from "../../components-new/Thumbnail";
import { AddBulkUsers } from "./add-bulk-user";
import ActiveDevices from "../../pages/update-profile/active-devices";
import AiAssignModal from "../../components/Ai-assign-modal";

const UsersList = observer((props: any) => {
    const { users, onRefresh } = props;
    const { t } = useTranslation(["user_management", "survey_management"]);
    const rootStore = useStore();
    const { userStore, voterListStore, aiGentoolStore, projectStore } = rootStore;
    const { showVoterlist, FilterStatus } = voterListStore;
    const { view_more_booths } = aiGentoolStore;
    const userProfile = userStore.user;
    const role = userProfile["role"];
    const [aiAssignOpen, setAiAssignOpen] = useState(false);
    const [voterProfile, setVoterProfile] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const navigate = useNavigate();
    const { projectid } = useParams();
    // useEffect(() => {
    //   onRefresh();
    // }, [userStore.refreshUsersList]);

    const [activeDeviceUser, setActiveDeviceUser] = useState<any>(null);

    const groupedUsers = groupObjectsByKey(users);

    const close_Filter_modal = () => {
        voterListStore.setFilterStatus(false);
        voterListStore.setShowVoterlist(true);
        voterListStore.setFilterVal(null);
        voterListStore.setSelectedBooths([]);
    };

    const close_bulk_modal = () => {
        userStore.set_add_bulk_user(false);
    };

    const close_voterList = () => {
        voterListStore.setShowVoterlist(false);
    };

    const onVoterSelection = (voter) => {
        voterListStore.update_connect_user_to_voterlist_modal(true);
    };

    const handleUserOnClick = (user) => {
        userStore.setUserId(user.id);
        userStore.setActiveUser(user);
        userStore.setUserDetailsMode(true);
    };

    const handleOTPClick = (e, user) => {
        htmlValidation(e);
        const text = navigator.clipboard.writeText(`${user.name} - ${user.otp}`);
        if (text) {
            toast.success(`${user.name} - ${user.otp} copied`);
        }
    };

    const handleBulkClick = () => {
        userStore.set_add_bulk_user(true);
    };

    const handleBanUser = (e, user) => {
        htmlValidation(e);
        userStore.setActiveUser(user);
        userStore.update_user_store_prop("ban_user_modal", true);
    };

    const handleDeleteUser = (e, user) => {
        htmlValidation(e);
        userStore.setActiveUser(user);
        userStore.update_user_store_prop("remove_user_modal", true);
    };

    const handleEditUser = (e, user) => {
        htmlValidation(e);
        userStore.setActiveUser(user);
        userStore.update_user_store_prop("edit_user_modal", true);
    };

    const handleViewMore = (user) => {
        setSelectedUser(user);
        aiGentoolStore.show_view_more_booths(true);
    };

    const handleCloseTailwindModal = () => {
        aiGentoolStore.show_view_more_booths(false);
        setSelectedUser(null);
    };

    const handleOrgChartClick = () => {
        navigate(`/project/${projectid}/usermanagement/orgchart`);
    };

    const handleUserDevices = async (e, user) => {
        try {
            const response = await userStore.getUserDevices(user.id);

            if (response?.ok) {
                userStore.set_active_devices(response.data.devices || []);

                setActiveDeviceUser(user);
            }
        } catch (e) {
            console.log("Failed to fetch devices", e);
        }
    };

    const floatingMenuOptions = [
        {
            title: "Ban User",
            icon: "fas fa-ban text-red-700",
            onClick: (e, user) => handleBanUser(e, user),
        },
        {
            title: "Delete",
            icon: "fas fa-trash text-orange-500",
            onClick: (e, user) => handleDeleteUser(e, user),
        },
        {
            title: "Edit",
            icon: "fas fa-edit text-green-300",
            onClick: (e, user) => handleEditUser(e, user),
        },
        {
            title: "Active Devices",
            icon: "fa-solid fa-laptop",
            onClick: (e, user) => handleUserDevices(e, user),
        },
    ];
    const normalizeBoothId = (value: any) => {
        if (value == null || value === "") {
            return null;
        }

        const raw = typeof value === "object" ? (value.id ?? value.value) : value;

        if (raw == null || raw === "") {
            return null;
        }

        return String(raw).trim().padStart(3, "0");
    };

    const handleAiAssign = async (assignments: any[]) => {
        try {
            if (!Array.isArray(assignments) || assignments.length === 0) {
                toast.error("No booth assignments found");
                return;
            }

            const invalidAssignments = assignments.filter(
                (assignment) => !assignment?.user?.id || !assignment?.booth
            );

            if (invalidAssignments.length > 0) {
                throw new Error("Some booth assignments are invalid");
            }

            const boothOwners: Record<string, string> = {};

            for (const assignment of assignments) {
                const booth = String(assignment.booth).trim();
                const userId = String(assignment.user.id);

                if (boothOwners[booth] && boothOwners[booth] !== userId) {
                    throw new Error(`Booth ${booth} is being assigned to multiple users`);
                }

                boothOwners[booth] = userId;
            }

            const groupedAssignments: Record<string, { user: any; booths: string[] }> = {};

            for (const assignment of assignments) {
                const userId = String(assignment.user.id);
                const booth = normalizeBoothId(assignment.booth);

                if (!booth) {
                    continue;
                }

                const liveUser =
                    users?.find((item: any) => String(item.id) === userId) || assignment.user;

                if (!groupedAssignments[userId]) {
                    groupedAssignments[userId] = {
                        user: liveUser,
                        booths: [],
                    };
                }

                if (!groupedAssignments[userId].booths.includes(booth)) {
                    groupedAssignments[userId].booths.push(booth);
                }
            }

            const numericProjectId =
                projectStore.activeProject?.id ?? parseInt(String(projectid), 10);

            if (!numericProjectId) {
                throw new Error("Project id is missing");
            }

            for (const userId of Object.keys(groupedAssignments)) {
                const { user, booths: newBooths } = groupedAssignments[userId];

                const existingBooths = Array.isArray(user.parts)
                    ? user.parts
                          .map(normalizeBoothId)
                          .filter((boothId): boothId is string => Boolean(boothId))
                    : [];

                const updatedBooths = [...new Set([...existingBooths, ...newBooths])];

                const payload = {
                    projectid: numericProjectId,
                    userid: user.id,
                    hierarchy: updatedBooths,
                };

                const res = await userStore.updateUserHierarchy(payload);

                if (!res?.ok) {
                    throw new Error(`Failed to assign booths to ${user.name}`);
                }
            }

            if (onRefresh) {
                await onRefresh();
            }

            toast.success(
                `${assignments.length} booth assignment${
                    assignments.length !== 1 ? "s" : ""
                } completed successfully`
            );

            setAiAssignOpen(false);
        } catch (error: any) {
            console.error("❌ AI assignment failed:", error);
            toast.error(error?.message || "Failed to assign booths");
            throw error;
        }
    };
    const exportData =
        users
            ?.filter((user) => user.role !== "root" && user.role !== "client")
            ?.map((user) => ({
                name: user.name,
                role: safe_get(map_roles_by_key, user.role, ""),
                phonenum: user.phonenum,
                parts: user.parts?.join(", ") || "No Parts Assigned",
            })) || [];

    if (users && users?.length === 0) return null;

    return (
        <div className="mt-4 rounded-lg bg-white shadow-lg">
            <div className="flex flex-row items-center justify-between">
                <h2 className="p-4 text-lg font-bold text-indigo-500 md:text-2xl">{t("users")}</h2>
                <div className="mr-2 flex flex-row items-center justify-between gap-x-4">
                    <Button
                        startIcon={<i className={"fa-solid fa-arrows-rotate"} />}
                        color={"warning"}
                        onClick={onRefresh}
                        variant={"contained"}
                        sx={{
                            padding: "5px 25px",
                            "@media (min-width: 600px)": {
                                padding: "10px 25px",
                            },
                        }}
                    >
                        Refresh
                    </Button>
                    {(role === "root" || role === "client") && (
                        <Button
                            startIcon={<i className="fa-solid fa-sitemap" />}
                            color={"secondary"}
                            onClick={handleOrgChartClick}
                            variant={"contained"}
                            sx={{
                                padding: "5px 25px",
                                "@media (min-width: 600px)": {
                                    padding: "10px 25px",
                                },
                            }}
                        >
                            OrgChart
                        </Button>
                    )}
                    {/*<Button*/}
                    {/*  startIcon={<i className="fa-solid fa-circle-plus" />}*/}
                    {/*  color={"primary"}*/}
                    {/*  onClick={(event) => {*/}
                    {/*    userStore.update_user_store_prop("add_new_users_modal", true);*/}
                    {/*  }}*/}
                    {/*  variant={"contained"}*/}
                    {/*  sx={{*/}
                    {/*    padding: "5px 20px",*/}
                    {/*    "@media (min-width: 600px)": {*/}
                    {/*      padding: "10px 25px",*/}
                    {/*    },*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  Add Users*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    startIcon={<i className="fa-solid fa-circle-plus" />}*/}
                    {/*    color={"primary"}*/}
                    {/*    onClick={handleBulkClick}*/}
                    {/*    variant={"contained"}*/}
                    {/*    sx={{*/}
                    {/*        padding: "5px 20px",*/}
                    {/*        "@media (min-width: 600px)": {*/}
                    {/*            padding: "10px 25px",*/}
                    {/*        },*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Add Users*/}
                    {/*</Button>*/}
                    {/*<DownloadAsExcelSheet*/}
                    {/*    data={exportData}*/}
                    {/*    cols={excel_user_parts_col_def}*/}
                    {/*    name="User_Part_Assignment"*/}
                    {/*    variant="button"*/}
                    {/*    buttonLabel="Export Parts"*/}
                    {/*/>*/}
                    <Button
                        startIcon={<i className="fa-solid fa-circle-plus" />}
                        color="primary"
                        onClick={handleBulkClick}
                        variant="contained"
                        sx={{
                            padding: "5px 20px",
                            "@media (min-width: 600px)": {
                                padding: "10px 25px",
                            },
                        }}
                    >
                        Add Users
                    </Button>
                    <Button
                        startIcon={<i className="fa-solid fa-wand-magic-sparkles" />}
                        color="secondary"
                        onClick={() => setAiAssignOpen(true)}
                        variant="contained"
                        sx={{
                            padding: "5px 20px",
                            "@media (min-width: 600px)": {
                                padding: "10px 25px",
                            },
                        }}
                    >
                        AI Assign
                    </Button>

                    <DownloadAsExcelSheet
                        data={exportData}
                        cols={excel_user_parts_col_def}
                        name="User_Part_Assignment"
                        variant="button"
                        buttonLabel="Export Parts"
                        buttonProps={{
                            variant: "contained",
                            startIcon: <i className="fa-solid fa-file-excel" />,
                            sx: {
                                padding: "5px 20px",
                                "@media (min-width:400px)": {
                                    padding: "8px 20px",
                                },
                                backgroundColor: "#1D6F42",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#155A36",
                                },
                            },
                        }}
                    />
                </div>
            </div>

            {groupedUsers &&
                Object.keys(groupedUsers).map((key) => {
                    if (key === "root" && role === "client") return;
                    return (
                        <div key={key}>
                            <div className="flex items-center px-4 py-2">
                                <Typography
                                    color="primary"
                                    variant="h6"
                                    sx={{
                                        fontSize: "18px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        "@media (max-width: 600px)": {
                                            fontSize: "15px",
                                        },
                                    }}
                                    className="font-semibold"
                                >
                                    {safe_get(map_roles_by_key, key, "")}
                                    <span className="ml-2 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-bold text-indigo-600">
                                        {groupedUsers[key]?.length || 0}{" "}
                                        {groupedUsers[key]?.length === 1 ? "User" : "Users"}
                                    </span>
                                </Typography>
                            </div>

                            <ul
                                role="list"
                                className="mx-auto mb-2 grid max-w-2xl grid-cols-1 gap-6 px-4 text-center sm:mb-4 sm:grid-cols-3 md:grid-cols-4 lg:mx-0 lg:max-w-none lg:grid-cols-4 xl:grid-cols-6"
                            >
                                {groupedUsers[key].map((user) => {
                                    return (
                                        <li
                                            key={user.id}
                                            className="group relative flex cursor-pointer flex-col items-center gap-y-2 rounded-xl border border-gray-300 bg-gray-100 p-4 shadow-md"
                                            onClick={() => handleUserOnClick(user)}
                                        >
                                            <div className="flex flex-shrink-0 flex-row justify-between">
                                                <Avatar
                                                    sx={{
                                                        width: 100,
                                                        height: 100,
                                                    }}
                                                    alt={safe_get(user, "name", null)}
                                                >
                                                    {user?.profileimage && (
                                                        <ThumbnailImage
                                                            key={user.id}
                                                            s3Key={user.profileimage}
                                                            bucket="urn-assets"
                                                            width={100}
                                                            height={100}
                                                        />
                                                    )}
                                                </Avatar>

                                                {/*<div className={"absolute right-0"}>*/}
                                                {/*    <FloatingMenu items={floatingMenuOptions} data={user} />*/}
                                                {/*</div>*/}

                                                <div className="absolute right-3 top-3 flex items-center gap-2">
                                                    <FloatingMenu items={floatingMenuOptions} data={user} />
                                                </div>
                                            </div>

                                            <Typography variant={"h6"} color={"primary"}>
                                                {user.name}
                                            </Typography>
                                            <Typography variant={"body1"} color={""}>
                                                {safe_get(map_roles_by_key, user.role, "")} ({user.id})
                                            </Typography>
                                            <Typography variant={"body1"}>{user.phonenum}</Typography>

                                            {user?.parts?.length > 0 &&
                                                user.role !== "root" &&
                                                user.role !== "client" && (
                                                    <div className="mt-5 w-full">
                                                        <div className="flex flex-col items-center text-center">
                                                            {/* Label */}
                                                            <p className="text-[13px] font-medium uppercase tracking-wide text-gray-700">
                                                                Assigned Parts
                                                            </p>

                                                            <div className="mt-2 flex flex-row items-center justify-center gap-2">
                                                                {user?.parts
                                                                    ?.slice(0, 3)
                                                                    .map((booth, index) => (
                                                                        <span
                                                                            key={index}
                                                                            className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-[13px] font-medium text-indigo-600 shadow-sm"
                                                                        >
                                                                            {booth}
                                                                        </span>
                                                                    ))}

                                                                {user?.parts?.length > 3 && (
                                                                    <span
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleViewMore(user);
                                                                        }}
                                                                        className="cursor-pointer rounded-full bg-gray-100 px-3 py-1.5 text-[15px] font-bold text-gray-600 transition-all duration-200 hover:bg-indigo-100 hover:text-indigo-600"
                                                                        title="View all assigned booths"
                                                                    >
                                                                        ...
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            {user.otp ? (
                                                <Stack
                                                    direction={"row"}
                                                    className="mr-2 flex-wrap items-center justify-between text-sm text-gray-700"
                                                >
                                                    <KeyIcon
                                                        className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                                                        aria-hidden="true"
                                                    />
                                                    <Typography variant={"body2"} className="truncate">
                                                        OTP {user.otp}
                                                    </Typography>
                                                    <i
                                                        className="fa-solid fa-copy cursor-pointer px-4 text-xl text-blue-300"
                                                        onClick={(e) => handleOTPClick(e, user)}
                                                    />
                                                </Stack>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                            <Divider />
                            {/*<button*/}
                            {/*    onClick={(e) => {*/}
                            {/*        e.stopPropagation();*/}

                            {/*        handleUserDevices(user);*/}
                            {/*    }}*/}
                            {/*    className="group flex items-center gap-2 rounded-full border border-indigo-100 bg-white px-3 py-2 shadow-sm transition-all duration-200 hover:border-indigo-300 hover:shadow-md"*/}
                            {/*>*/}
                            {/*    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">*/}
                            {/*        <i className="fa-solid fa-laptop" />*/}
                            {/*    </div>*/}

                            {/*    <span className="hidden text-xs font-semibold text-indigo-600 sm:block">*/}
                            {/*        Devices*/}
                            {/*    </span>*/}
                            {/*</button>*/}
                        </div>
                    );
                })}

            <TailwindModal isOpen={view_more_booths} onClose={handleCloseTailwindModal}>
                <div className="rounded-2xl bg-gradient-to-b from-white to-gray-50 p-8 shadow-lg">
                    {/* Header */}
                    <div className="mb-6 flex flex-col items-center text-center">
                        <p className="text-sm text-gray-500">
                            <span className="pr-2 font-medium text-indigo-600">{selectedUser?.name}</span>
                            <Typography>Parts</Typography>
                        </p>
                        <div className="mt-3 h-1 w-12 rounded-full bg-indigo-500"></div>
                    </div>

                    {selectedUser?.parts?.length > 0 ? (
                        <div className="grid max-h-[60vh] grid-cols-2 gap-4 overflow-y-auto p-2 sm:grid-cols-3 md:grid-cols-4">
                            {selectedUser.parts.map((booth, index) => (
                                <div
                                    key={index}
                                    className="rounded-xl border border-indigo-100 bg-white px-4 py-2 text-center text-sm font-medium text-indigo-600 shadow-sm transition-all duration-200 hover:bg-indigo-50"
                                >
                                    Part {booth}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-10">
                            <p className="text-sm italic text-gray-500">No Parts assigned to this user.</p>
                        </div>
                    )}

                    <div className="mt-8 flex justify-center">
                        <button
                            onClick={handleCloseTailwindModal}
                            className="rounded-lg bg-indigo-600 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-indigo-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </TailwindModal>

            <UpdateUser />
            <RemoveUser />
            <BanUser />
            <AiAssignModal
                open={aiAssignOpen}
                onClose={() => setAiAssignOpen(false)}
                users={users}
                onAssign={handleAiAssign}
            />
            <SideOverlay onClose={close_voterList} show={showVoterlist} title="Search Voters" size={"5xl"}>
                <SearchVoterList />
                <VoterListWithRadioButton onVoterSelection={onVoterSelection} />
            </SideOverlay>
            <SideOverlay
                children={<FilterOverLay />}
                onClose={close_Filter_modal}
                show={FilterStatus}
                title="Filter VoterSearch"
            />
            <SideOverlay
                children={<AddBulkUsers />}
                onClose={close_bulk_modal}
                show={userStore.add_bulk_user}
                title="Add Users"
                size={"medium"}
            />
            <SideOverlay
                show={!!activeDeviceUser}
                onClose={() => setActiveDeviceUser(null)}
                title={activeDeviceUser ? `${activeDeviceUser.name} Devices` : "Active Devices"}
                size={"large"}
            >
                <ActiveDevices isUserManagement activeDeviceUser={activeDeviceUser} />
            </SideOverlay>
        </div>
    );
});

export default memo(UsersList);
